/** Dashboard — реальные KPI + Recharts sales chart + Phase 8 widgets. */

import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  DollarSign,
  Package,
  ShoppingBag,
  ShoppingCart,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DonutChart } from "@tremor/react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { formatPrice } from "@/lib/utils";

type Period = "today" | "last_7d" | "last_30d" | "last_90d";

const PERIOD_LABELS: Record<Period, string> = {
  today: "Сегодня",
  last_7d: "7 дней",
  last_30d: "30 дней",
  last_90d: "90 дней",
};

interface KpiCardProps {
  title: string;
  value: string;
  changePercent?: number | null;
  icon: React.ComponentType<{ className?: string }>;
}

function KpiCard({ title, value, changePercent, icon: Icon }: KpiCardProps): JSX.Element {
  const isPositive = changePercent != null && changePercent >= 0;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {changePercent != null && (
          <div
            className={`text-xs flex items-center mt-1 ${isPositive ? "text-green-600" : "text-red-600"}`}
          >
            {isPositive ? (
              <ArrowUp className="w-3 h-3 mr-1" />
            ) : (
              <ArrowDown className="w-3 h-3 mr-1" />
            )}
            {Math.abs(changePercent).toFixed(1)}% vs предыдущий период
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Phase 8: новые виджеты ──────────────────────────────────────────────

interface TopProduct {
  product_id: number;
  name: string;
  image_url: string | null;
  units_sold: number;
  revenue: number;
}

function TopProductsCard({ period }: { period: Period }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-top-products", period],
    queryFn: async () => {
      const { data } = await api.get<TopProduct[]>(
        `/analytics/top-products?period=${period}&limit=10`,
      );
      return data;
    },
    staleTime: 60_000,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Star className="w-4 h-4" />
          Топ-10 товаров
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-4 pb-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            Нет данных за период
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((p) => (
              <li
                key={p.product_id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50"
              >
                <div className="w-10 h-10 rounded bg-muted flex-shrink-0 overflow-hidden">
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/products/edit/${p.product_id}`}
                    className="text-sm font-medium line-clamp-1 hover:underline"
                  >
                    {p.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {p.units_sold} шт.
                  </p>
                </div>
                <div className="text-sm font-semibold whitespace-nowrap">
                  {formatPrice(p.revenue)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface CategorySlice {
  category_id: number;
  revenue: number;
  units_sold: number;
}

function CategoryDonutCard({ period }: { period: Period }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-sales-by-category", period],
    queryFn: async () => {
      const { data } = await api.get<CategorySlice[]>(
        `/analytics/sales-by-category?period=${period}`,
      );
      return data;
    },
    staleTime: 60_000,
  });

  const chartData = (data ?? []).map((s) => ({
    name: `Категория #${s.category_id}`,
    revenue: Math.round(s.revenue / 100), // тийины → сумы для красивых tick'ов
  }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Продажи по категориям</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-56" />
        ) : chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет данных за период</p>
        ) : (
          <DonutChart
            data={chartData}
            category="revenue"
            index="name"
            valueFormatter={(n) =>
              new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(
                n,
              ) + " сум"
            }
            className="h-56"
          />
        )}
      </CardContent>
    </Card>
  );
}

interface LowStockItem {
  id: number;
  name: string;
  sku: string;
  stock_quantity: number;
  base_price: number;
  category_id: number | null;
}

function LowStockListCard(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-low-stock"],
    queryFn: async () => {
      const { data } = await api.get<LowStockItem[]>(
        `/analytics/low-stock?threshold=10&limit=10`,
      );
      return data;
    },
    staleTime: 60_000,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Низкий остаток
          </span>
          <Link
            to="/products"
            className="text-xs text-primary hover:underline font-normal"
          >
            Все →
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-4 pb-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-8" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            Все остатки в норме
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/products/edit/${p.id}`}
                    className="text-sm font-medium line-clamp-1 hover:underline"
                  >
                    {p.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">{p.sku}</p>
                </div>
                <span
                  className={`text-sm font-semibold ${p.stock_quantity === 0 ? "text-red-600" : "text-amber-600"}`}
                >
                  {p.stock_quantity}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface AbandonedCart {
  client_id: number;
  phone: string;
  full_name: string | null;
  items_count: number;
  total: number;
  last_activity: string | null;
}

function AbandonedCartsCard(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-abandoned-carts"],
    queryFn: async () => {
      const { data } = await api.get<AbandonedCart[]>(
        `/analytics/abandoned-carts?hours=24&limit=10`,
      );
      return data;
    },
    staleTime: 60_000,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" />
          Брошенные корзины (24ч)
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-4 pb-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            Нет брошенных корзин
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.map((c) => (
              <li
                key={c.client_id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/clients/show/${c.client_id}`}
                    className="text-sm font-medium hover:underline"
                  >
                    {c.full_name || c.phone}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {c.items_count} тов. · {c.phone}
                  </p>
                </div>
                <div className="text-sm font-semibold whitespace-nowrap">
                  {formatPrice(c.total)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [period, setPeriod] = useState<Period>("last_30d");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard", period],
    queryFn: async () => {
      const { data } = await api.get(`/analytics/dashboard?period=${period}`);
      return data;
    },
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading text-primary">
            {t("dashboard.welcome", { name: user?.full_name ?? "" })}
          </h1>
        </div>
        <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
              <SelectItem key={p} value={p}>
                {PERIOD_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading || !data ? (
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title={t("dashboard.kpiRevenue")}
              value={formatPrice(data.revenue.current)}
              changePercent={data.revenue.change_percent}
              icon={DollarSign}
            />
            <KpiCard
              title={t("dashboard.kpiOrders")}
              value={String(data.orders.current)}
              changePercent={data.orders.change_percent}
              icon={ShoppingBag}
            />
            <KpiCard
              title={t("dashboard.kpiAov")}
              value={formatPrice(data.avg_order_value.current)}
              changePercent={data.avg_order_value.change_percent}
              icon={TrendingUp}
            />
            <KpiCard
              title={t("dashboard.kpiClients")}
              value={String(data.new_clients.current)}
              changePercent={data.new_clients.change_percent}
              icon={Users}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Продажи по дням</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.sales_by_day}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(v) => new Date(v).getDate().toString()}
                        className="text-xs"
                      />
                      <YAxis
                        tickFormatter={(v) => `${(v / 100_000_000).toFixed(0)}M`}
                        className="text-xs"
                      />
                      <Tooltip
                        formatter={(value: number) => formatPrice(value)}
                        labelFormatter={(v) => new Date(v).toLocaleDateString("ru-RU")}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#1e73e8"
                        strokeWidth={2}
                        dot={{ fill: "#1e73e8", r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    Pending заказы
                    <Link
                      to="/orders?status=created"
                      className="text-xs text-primary hover:underline"
                    >
                      Все →
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {data.pending_orders}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Требуют обработки
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Низкий остаток (счёт)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-600">
                    {data.low_stock_products}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Товаров с остатком &lt; 10
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Phase 8 widgets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopProductsCard period={period} />
            <CategoryDonutCard period={period} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LowStockListCard />
            <AbandonedCartsCard />
          </div>
        </>
      )}
    </div>
  );
}
